import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight, CalendarClock, GraduationCap, Laptop, Ticket, UserMinus } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { useToast } from '../components/toast';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Input,
  PageHeader,
  PageLoader,
  Pagination,
  Select,
  StatCard,
  Table,
  Td,
  Th,
} from '../components/ui';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate, formatRelative, PERSON_TYPE_LABEL } from '../lib/format';
import { useDebouncedValue } from '../lib/hooks';
import type { PersonType } from '../lib/types';

type DepartureReason = 'INACTIVE' | 'ALUMNI' | 'PAST_STUDENT' | 'ROLE' | 'TRANSFERRED' | 'ACCOUNT_SUSPENDED' | 'GRADUATING';

const REASON_LABEL: Record<DepartureReason, string> = {
  INACTIVE: 'No longer in the roster',
  ALUMNI: 'Alumni',
  PAST_STUDENT: 'Past student',
  ROLE: 'A role says they’ve left',
  TRANSFERRED: 'Left the district',
  ACCOUNT_SUSPENDED: 'Google account suspended',
  GRADUATING: 'Graduating this year',
};

/** Still here but on the way out reads differently from already gone. */
const REASON_TONE: Partial<Record<DepartureReason, 'red' | 'amber' | 'blue'>> = {
  INACTIVE: 'red',
  TRANSFERRED: 'red',
  ACCOUNT_SUSPENDED: 'red',
  ALUMNI: 'amber',
  PAST_STUDENT: 'amber',
  ROLE: 'amber',
  GRADUATING: 'blue',
};

interface Departure {
  person: {
    id: string;
    firstName: string;
    lastName: string;
    preferredName: string | null;
    email: string | null;
    type: PersonType;
    status: string;
    gradYear: number | null;
  };
  reasons: DepartureReason[];
  noticedAt: string;
  checkouts: { id: string; assetId: string; assetTag: string; assetName: string; checkedOutAt: string; dueAt: string | null; overdue: boolean; kind: string }[];
  ticket: { id: string; number: number; status: string } | null;
}

interface DeparturePage {
  items: Departure[];
  total: number;
  page: number;
  pageSize: number;
  summary: { people: number; devices: number; overdue: number; gone: number; graduating: number; withTicket: number; withoutTicket: number };
}

const name = (person: Departure['person']) => `${person.preferredName || person.firstName} ${person.lastName}`;

/**
 * Who is leaving, or has left, and still has a device. The school's own records decide who is on the list - the roster
 * no longer listing them, a role that says they've gone, a transfer out, a suspended account, or a graduating class -
 * and each row says which, so nobody has to guess why a name is here.
 */
export function DeparturesPage() {
  const { can } = useAuth();
  const canWrite = can('TECHNICIAN');
  const toast = useToast();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({ search: '', reason: '', type: '', goneOnly: false, sort: 'overdue', page: 1 });
  const search = useDebouncedValue(filters.search.trim(), 300);

  const query = useQuery({
    queryKey: ['departures', { ...filters, search }],
    queryFn: () =>
      api.get<DeparturePage>('/departures', {
        search: search || undefined,
        reason: filters.reason || undefined,
        type: filters.type || undefined,
        goneOnly: filters.goneOnly ? 'true' : undefined,
        sort: filters.sort,
        page: filters.page,
      }),
  });

  const openTicket = useMutation({
    mutationFn: (person: Departure['person']) => api.post<{ id: string; number: number }>(`/departures/${person.id}/ticket`, {}),
    onSuccess: (ticket, person) => {
      void queryClient.invalidateQueries({ queryKey: ['departures'] });
      void queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      toast.success(`Opened #${ticket.number} to collect from ${name(person)}`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (query.isLoading) return <PageLoader />;
  if (query.error || !query.data) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const { items, summary, total, pageSize } = query.data;
  const set = (patch: Partial<typeof filters>) => setFilters((current) => ({ ...current, page: 1, ...patch }));

  return (
    <>
      <PageHeader
        title="Departures"
        description="People your records say are leaving or have left, and the devices they still have. Collect them before they walk out of the building."
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="People to chase" value={summary.people} sub={`${summary.gone} already gone`} icon={<UserMinus />} tone={summary.people ? 'amber' : 'brand'} />
        <StatCard label="Devices out" value={summary.devices} sub={`${summary.overdue} overdue`} icon={<Laptop />} tone={summary.overdue ? 'red' : 'brand'} />
        <StatCard label="Graduating" value={summary.graduating} sub="Still here, leaving this year" icon={<GraduationCap />} />
        <StatCard
          label="No ticket yet"
          value={summary.withoutTicket}
          sub={`${summary.withTicket} being chased`}
          icon={<Ticket />}
          tone={summary.withoutTicket ? 'amber' : 'brand'}
        />
      </div>

      <Card className="mt-6">
        <CardHeader
          title="Who still has something"
          description={`${total} ${total === 1 ? 'person' : 'people'} with a device out. Opening a ticket puts it on the help desk; one ticket per person.`}
        />
        <div className="flex flex-wrap items-center gap-3 px-5 pt-5">
          <Input className="w-64" placeholder="Search name, email, or asset tag" value={filters.search} onChange={(event) => set({ search: event.target.value })} />
          <Select value={filters.reason} onChange={(event) => set({ reason: event.target.value })}>
            <option value="">Any reason</option>
            {(Object.keys(REASON_LABEL) as DepartureReason[]).map((reason) => (
              <option key={reason} value={reason}>
                {REASON_LABEL[reason]}
              </option>
            ))}
          </Select>
          <Select value={filters.type} onChange={(event) => set({ type: event.target.value })}>
            <option value="">Students and staff</option>
            {(Object.keys(PERSON_TYPE_LABEL) as PersonType[]).map((type) => (
              <option key={type} value={type}>
                {PERSON_TYPE_LABEL[type]}
              </option>
            ))}
          </Select>
          <Select value={filters.sort} onChange={(event) => set({ sort: event.target.value })}>
            <option value="overdue">Most urgent first</option>
            <option value="devices">Most devices first</option>
            <option value="departed">Left most recently</option>
            <option value="name">By name</option>
          </Select>
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="size-4 rounded border-slate-300 accent-brand-700"
              checked={filters.goneOnly}
              onChange={(event) => set({ goneOnly: event.target.checked })}
            />
            Already gone only
          </label>
        </div>

        {items.length === 0 ? (
          <EmptyState
            icon={<UserMinus />}
            title="Nobody to chase"
            description="Everyone who has left has handed their devices back. People show up here as soon as the roster says they’re gone."
          />
        ) : (
          <Table className="mt-4">
            <thead>
              <tr>
                <Th>Person</Th>
                <Th>Why</Th>
                <Th>Still has</Th>
                <Th>Noticed</Th>
                <Th className="text-right">Collect</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((row) => (
                <tr key={row.person.id} className="align-top hover:bg-slate-50/60">
                  <Td>
                    <Link to={`/people/${row.person.id}`} className="font-medium text-slate-900 hover:text-brand-700">
                      {name(row.person)}
                    </Link>
                    <p className="text-xs text-slate-500">
                      {PERSON_TYPE_LABEL[row.person.type]}
                      {row.person.gradYear ? ` · ${row.person.gradYear}` : ''}
                      {row.person.email ? ` · ${row.person.email}` : ''}
                    </p>
                  </Td>
                  <Td>
                    <span className="flex flex-wrap gap-1">
                      {row.reasons.map((reason) => (
                        <Badge key={reason} tone={REASON_TONE[reason]}>
                          {REASON_LABEL[reason]}
                        </Badge>
                      ))}
                    </span>
                  </Td>
                  <Td>
                    <ul className="space-y-1">
                      {row.checkouts.map((checkout) => (
                        <li key={checkout.id} className="flex flex-wrap items-center gap-2 text-sm">
                          <Link to={`/assets/${checkout.assetId}`} className="font-medium text-brand-700 hover:underline">
                            {checkout.assetTag}
                          </Link>
                          <span className="text-slate-600">{checkout.assetName}</span>
                          {checkout.kind === 'LOANER' && <Badge>Loaner</Badge>}
                          {checkout.dueAt ? (
                            <span className={checkout.overdue ? 'text-xs font-medium text-red-700' : 'text-xs text-slate-500'}>
                              {checkout.overdue ? 'Overdue ' : 'Due '}
                              {formatDate(checkout.dueAt)}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">No due date</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </Td>
                  <Td className="text-xs whitespace-nowrap text-slate-500">
                    <span className="flex items-center gap-1">
                      <CalendarClock className="size-3.5" />
                      {formatRelative(row.noticedAt)}
                    </span>
                  </Td>
                  <Td className="text-right whitespace-nowrap">
                    {row.ticket ? (
                      <Link to={`/work-orders/${row.ticket.id}`}>
                        <Button size="sm" variant="ghost" icon={<Ticket />}>
                          #{row.ticket.number}
                        </Button>
                      </Link>
                    ) : (
                      canWrite && (
                        <Button
                          size="sm"
                          icon={<Ticket />}
                          loading={openTicket.isPending && openTicket.variables?.id === row.person.id}
                          onClick={() => openTicket.mutate(row.person)}
                        >
                          Open a ticket
                        </Button>
                      )
                    )}
                    {canWrite && (
                      <Link to="/station">
                        <Button size="sm" variant="ghost" icon={<ArrowLeftRight />}>
                          Check in
                        </Button>
                      </Link>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        <Pagination page={filters.page} pageSize={pageSize} total={total} onPage={(page) => setFilters((current) => ({ ...current, page }))} />
      </Card>

      <p className="mt-6 text-xs text-slate-500">
        Tickets can be opened for you: under <Link to="/automation" className="font-medium text-brand-700 hover:underline">Automation › Smart rules</Link>, add a
        rule with “Someone left and still has a device”. It waits a few days after the roster changes, so a sync blip
        doesn’t open tickets, and opens one ticket per person however many devices they have.
      </p>
    </>
  );
}
